/* The two plan-system illustrations — `docs/design/plan-propozycje-2026-09-21/plan.css`, the
 * `.viz-cal` and `.viz-seq` blocks, transcribed.
 *
 * **There is no `@keyframes` in this file, deliberately.** The mock animates both loops in CSS;
 * this app drives them through `motion` so `prefers-reduced-motion` can arrive as a parameter
 * (`planSystemLoop.ts` carries the whole reasoning). What is left here is the drawing: the boxes,
 * the inks and the two ring overlays that stand in for the mock's `border-color` tweens.
 *
 * Every colour, space, radius and type value is a token. Widths and heights are layout. */

.viz {
  display: flex;
  flex-direction: column;
}

/* ── Illustration 1: the calendar week ──────────────────────────────────────────────────────── */

.calendar {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--space-5px);
  width: 100%;
}

.tile {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: var(--h-viz-tile);
  padding: var(--space-7px) var(--space-0) var(--space-6px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

/* The struck day keeps its place in the week — that is the whole argument the drawing makes. */
.miss {
  border-style: dashed;
  border-color: var(--border-2);
}

.due {
  background: var(--bg-card);
}

.day {
  font-size: var(--fs-viz-day);
  font-weight: var(--fw-bold);
  line-height: var(--lh-viz-line);
  color: var(--text-3);
}

.due .day {
  color: var(--text);
}

.name {
  font-size: var(--fs-viz-name);
  font-weight: var(--fw-bold);
  line-height: var(--lh-viz-line);
  color: var(--text-3);
}

.done .name {
  color: var(--text-2);
}

.miss .name {
  text-decoration: line-through;
}

.free .name {
  color: var(--pop-blue);
}

.due .name {
  color: var(--text);
}

.mark {
  display: grid;
  place-items: center;
  width: var(--sz-viz-mark);
  height: var(--sz-viz-mark);
  background: var(--bg-sunken);
  border-radius: var(--radius-xs);
}

.mark svg {
  width: var(--space-3);
  height: var(--space-3);
}

.done .mark {
  color: var(--on-accent);
  background: var(--good);
}

.miss .mark {
  color: var(--danger);
  background: var(--viz-miss-wash);
}

.free .mark {
  background: var(--viz-free-wash);
  box-shadow: var(--shadow-viz-free-ring);
}

.due .mark {
  background: var(--text);
}

/* The mark starts hidden, because the loop's first keyframe is where it pops in FROM. A still
 * viewer never sees this state: `runPlanSystemLoop` parks the last frame, where it is landed. */
.done .mark,
.miss .mark,
.free .mark,
.due .mark {
  opacity: 0;
  transform: scale(0.4);
}

.frame {
  position: absolute;
  top: var(--offset-viz-frame);
  left: var(--offset-viz-frame);
  z-index: 1;
  box-sizing: border-box;
  width: calc((100% - var(--space-5px) * 4) / 5 + var(--space-6px));
  height: var(--h-viz-frame);
  pointer-events: none;
  border: var(--border-ring) solid var(--text);
  border-radius: var(--radius-13px);
}

/* ── Illustration 2: the self-paced sequence ────────────────────────────────────────────────── */

.sequence {
  position: relative;
  padding-top: var(--space-13px);
}

.seqRow {
  display: flex;
  gap: var(--space-3px);
  align-items: center;
}

.seqDay {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: var(--w-viz-day);
  height: var(--h-viz-tile);
  padding: var(--space-7px) var(--space-0) var(--space-6px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.seqNext {
  background: var(--bg-card);
}

.seqNext .day {
  color: var(--text);
}

/* ── The two rings that replace the mock's `border-color` tween ─────────────────────────────
 * `seq-activeframe` takes day one's border from the accent back to the hairline, and
 * `seq-wakeborder` takes day two's from the hairline to the ink. Motion resolves a `var()` in a
 * keyframe once, at the start, so a theme switch under a running loop would leave either tween
 * on the previous theme's colours. These carry their colours in CSS — where the theme class
 * already governs them — and cross-fade under `opacity` instead. */

/* The ink is passed in as a custom property rather than written on the `border` shorthand: the
 * value gate reads real property names only, and it refuses `transparent` on a border (it cannot
 * tell a deliberately invisible ring from a forgotten colour). A custom property is where the two
 * states are stated, and the rings below override it. `-1px` is a NAMED negative offset for the
 * same reason `--ph-space-gutter-out` is one: the gate rejects a `calc` outright. */
.seqRing {
  --ring-ink: transparent;

  position: absolute;
  inset: var(--offset-viz-ring);
  pointer-events: none;
  border: var(--border-hairline) solid var(--ring-ink);
  border-radius: var(--radius-sm);
}

.seqDone .seqRing {
  --ring-ink: var(--accent);
}

.seqNext .seqRing {
  --ring-ink: var(--text);

  opacity: 0;
  border-width: var(--border-thick);
}

.halo {
  position: absolute;
  inset: var(--offset-viz-halo);
  opacity: 0;
  pointer-events: none;
  background: var(--viz-halo);
  border-radius: var(--radius-17px);
}

.seqMark {
  position: relative;
  box-sizing: border-box;
  width: var(--sz-viz-mark);
  height: var(--sz-viz-mark);
  background: var(--bg-sunken);
  border-radius: var(--radius-xs);
}

.seqNext .seqMark {
  background: transparent;
  border: var(--border-ring) solid var(--text);
}

.seqCheck {
  position: absolute;
  inset: var(--space-0);
  display: grid;
  place-items: center;
  opacity: 0;
  color: var(--bg);
  background: var(--text);
  border-radius: var(--radius-xs);
  transform: scale(0.4);
}

.seqCheck svg {
  width: var(--space-3);
  height: var(--space-3);
}

.seqDone .name {
  color: var(--text-2);
}

/* Two labels stacked in one 10px box, so the swap changes ink and never the row's height. */
.seqNext .name {
  position: relative;
  height: var(--lh-viz-line);
}

.nameLayer {
  position: absolute;
  top: var(--space-0);
  right: var(--space-0);
  left: var(--space-0);
  text-align: center;
}

.nameWaiting {
  opacity: 0;
  color: var(--text);
}

.rest {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5px);
  align-items: center;
  min-width: var(--space-10);
}

.rule {
  width: 100%;
  height: var(--space-2px);
  background: var(--viz-rest-rule);
}

.restLabel {
  font-size: var(--fs-viz-rest);
  font-weight: var(--fw-bold);
  line-height: var(--lh-viz-line);
  color: var(--text-3);
  white-space: nowrap;
}

.cursor {
  position: absolute;
  top: var(--space-0);
  left: var(--offset-viz-cursor);
  width: var(--sz-viz-cursor);
  height: var(--sz-viz-cursor);
  background: var(--accent);
  border-radius: var(--r-pill);
}

/* ── The foot both illustrations share ──────────────────────────────────────────────────────── */

.foot {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-3) var(--space-0) var(--space-0);
  font-size: var(--fs-caption);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.figure {
  font-size: var(--fs-viz-figure);
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
}
